import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '../Autocomplete';
import { useFormContext } from '../Form';
import ApiClient from '@afosto/api/ApiClient';

const formatValue = (suggestions, value) => {
  if (typeof value === 'object' && Array.isArray(value)) {
    return value.map(item => suggestions.find(suggestion => suggestion.value === item));
  }

  return suggestions.find(suggestion => suggestion.value === value);
};

const AsyncAutocomplete = ({
  url,
  requestOptions,
  inputProps,
  name,
  onChange,
  formatOptions,
  multiple,
  value: providedValue,
  valueOnly,
  ...props
}) => {
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const formContext = useFormContext();
  const { setValue, watch } = formContext ?? {};
  const currentValue = providedValue ?? (watch && watch(name));
  const fallbackValue = multiple ? [] : null;
  const value = valueOnly ? formatValue(suggestions, currentValue) : currentValue;

  useEffect(() => {
    const fetchSuggestions = async suggestionsUrl => {
      setIsFetchingSuggestions(true);
      try {
        const response = await ApiClient.request({
          url: suggestionsUrl,
          ...requestOptions,
        });
        const { data = [] } = response;

        if (formatOptions) {
          setSuggestions(formatOptions(data));
        } else {
          setSuggestions(data);
        }
      } catch (err) {
        setSuggestions([]);
      } finally {
        setIsFetchingSuggestions(false);
      }
    };

    if (url) {
      fetchSuggestions(url).catch(() => {
        // Catch only.
      });
    }
  }, [formatOptions, url, requestOptions]);

  const handleSetValue = (ev, newValue) => {
    let updatedValue = newValue;

    if (typeof updatedValue === 'object' && valueOnly) {
      if (Array.isArray(updatedValue)) {
        updatedValue = updatedValue.map(item => item.value);
      } else {
        updatedValue = updatedValue.value;
      }
    }

    if (formContext && !onChange) {
      setValue(name, updatedValue);
      return;
    }

    if (onChange) {
      onChange(ev, updatedValue);
    }
  };

  /**
   * Handle the change of the Autocomplete
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = async (ev, newValue) => {
    try {
      if (newValue) {
        handleSetValue(ev, newValue);
      } else {
        handleSetValue(ev, null);
      }
    } catch (err) {
      handleSetValue(ev, null);
    }
  };

  return (
    <Autocomplete
      {...props}
      name={name}
      onChange={handleOnChange}
      options={suggestions}
      value={value ?? fallbackValue}
      loading={isFetchingSuggestions}
      multiple={multiple}
      inputProps={{
        ...(inputProps ?? {}),
      }}
    />
  );
};

AsyncAutocomplete.propTypes = {
  valueOnly: PropTypes.bool,
  url: PropTypes.string,
  requestOptions: PropTypes.object,
  ...Autocomplete.propTypes,
};

AsyncAutocomplete.defaultProps = {
  valueOnly: false,
  url: undefined,
  requestOptions: {},
  ...Autocomplete.defaultProps,
};

export default AsyncAutocomplete;
